import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  input,
  Output,
  signal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription, startWith, debounceTime, distinctUntilChanged } from 'rxjs';
import { getLogger } from '../../../../core/utils';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { DialogService, MessageType } from '../../services/dialog.service';
import { outputFromObservable, toObservable } from '@angular/core/rxjs-interop';
import { GoogleMapsService, NewLocation } from '../../services/google-maps.service';

@Component({
  selector: 'app-search-address',
  templateUrl: './search-address.component.html',
  styleUrl: './search-address.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SearchAddressComponent {
  public disabled = input(false, { transform: booleanAttribute });

  protected readonly addressCtrl = new FormControl('', { nonNullable: true });
  protected readonly addressOptions = signal<google.maps.GeocoderResult[]>([]);
  protected readonly isSearchingAddress = signal(false);
  protected readonly isSearchingMyLocation = signal(false);

  // Outputs
  @Output() public readonly newLocation = new EventEmitter<NewLocation>();
  public isSearching = outputFromObservable(toObservable(this.isSearchingMyLocation));

  private readonly logger = getLogger('SearchAddressComponent');
  private readonly subs = new Subscription();

  constructor(
    private readonly dialog: DialogService,
    private readonly googleMaps: GoogleMapsService,
  ) {
    this.subs.add(
      this.addressCtrl.valueChanges
        .pipe(startWith(''), debounceTime(500), distinctUntilChanged())
        .subscribe((val) => this.searchAddress(val)),
    );
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected clearAddress(): void {
    this.addressOptions.set([]);
    this.addressCtrl.setValue('');
  }

  protected async getCurrentLocation(): Promise<void> {
    this.isSearchingMyLocation.set(true);

    try {
      this.newLocation.emit(await this.googleMaps.getCurrentLocation());
    } catch (err) {
      this.logger.error(err);
      this.dialog.snack('There was an issue while getting the current location', MessageType.Error);
    } finally {
      this.isSearchingMyLocation.set(false);
    }
  }

  protected hasAddress(): boolean {
    return this.addressCtrl.value !== '';
  }

  protected optionSelected(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.value as google.maps.GeocoderResult;

    this.addressCtrl.setValue('', { emitEvent: false });

    this.newLocation.emit({
      address: this.googleMaps.isPlusCode(value) ? undefined : value.formatted_address,
      location: value.geometry.location,
    });
  }

  private async searchAddress(val: string): Promise<void> {
    if (typeof val !== 'string') {
      return;
    }

    try {
      this.isSearchingAddress.set(true);
      this.addressOptions.set([]);
      this.addressOptions.set(await this.googleMaps.geocode(val));
    } catch (err) {
      this.logger.error(err);
      this.dialog.snack('There was an issue while searching for the address', MessageType.Error);
    } finally {
      this.isSearchingAddress.set(false);
    }
  }
}
